import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useReviewQueue } from '../hooks/useReviewQueue';
import { useAuth } from '../hooks/useAuth';
import { useVerifyStore, type Claim } from '../features/verify/store';
import { Icon } from '../components/Icon';
import type { PostRow } from '../types/db';

const sevClass = (sev: number) => (sev === 1 ? '--red' : sev === 2 ? '--orange' : '--yellow');
const fmtAge = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  return `${Math.floor(min / 60)}h ago`;
};

function QueueCard({
  post,
  analyst,
  claims,
  onClaim,
  onComplete,
  resolved,
}: {
  post: PostRow;
  analyst: string;
  claims: Claim[];
  onClaim: () => boolean;
  onComplete: () => boolean;
  resolved: boolean;
}) {
  const list = claims;
  const mine = list.some((c) => c.analyst === analyst);
  const done = resolved;
  const [notice, setNotice] = useState('');

  const act = (fn: () => boolean, msg: string) => {
    if (fn()) setNotice(msg);
    else setNotice('Already claimed or slot full.');
  };

  return (
    <article className={`q-card${done ? ' q-card--done' : ''}`}>
      <div className="q-card__top">
        <span className={`event-type event-type${sevClass(post.severity)}`}>
          SEV-{post.severity} {post.flair}
        </span>
        <span className="mono dim">{fmtAge(post.created_at)}</span>
      </div>
      <Link to={`/post/${post.id}`} className="q-card__title">
        {post.title}
      </Link>
      <div className="q-card__meta">
        <span className="mono dim">{post.region}</span>
        <span className="mono dim">
          confidence {post.confidence ? Math.round(post.confidence * 100) : '?'}%
        </span>
      </div>
      <div className="q-card__slots">
        {[0, 1].map((i) => {
          const claim = list[i];
          return (
            <span key={i} className={`q-slot${claim ? (done ? ' q-slot--ok' : ' q-slot--taken') : ''}`}>
              {claim ? (
                <>
                  <Icon name="check" size={11} /> {claim.analyst}
                </>
              ) : (
                <span className="dim">Slot {i + 1} open</span>
              )}
            </span>
          );
        })}
      </div>
      <div className="q-card__actions">
        {done ? (
          <span className="event-type event-type--green">
            <Icon name="check" size={12} /> VERIFIED
          </span>
        ) : mine && !list.some((c) => c.analyst === analyst && (post.status === 'verified')) ? (
          <>
            <button className="chip chip--on" onClick={() => act(() => onComplete(), 'Verification submitted (+25 XP)')}>
              Complete verification
            </button>
            {notice && <span className="q-notice">{notice}</span>}
          </>
        ) : (
          <button className="chip chip--on" onClick={() => act(() => onClaim(), 'Claimed — slot locked (+10 XP)')}>
            <Icon name="target" size={12} /> Claim as {analyst}
          </button>
        )}
      </div>
    </article>
  );
}

export function VerifyPage() {
  const { data: posts, isLoading, error, refetch } = useReviewQueue();
  const { profile } = useAuth();
  const verify = useVerifyStore();
  const { leaderboard } = verify;
  const analyst = profile?.codename ?? 'GUEST';

  return (
    <div className="page" data-testid="verify-page">
      <section className="page-intro">
        <div>
          <span className="eyebrow">Verification arena</span>
          <h1>Review queue</h1>
          <p>
            Two analysts per signal. Claim a slot, verify the sources, close the case. First verified signal wins the
            reputation.
          </p>
        </div>
        <div className="live-status">
          <span className="live-dot live-dot--on" />
          {posts?.length ?? 0} OPEN
        </div>
      </section>

      <section className="verify-grid">
        <div className="q-list">
          {isLoading && <div className="empty-state">Loading queue…</div>}
          {error && (
            <div className="empty-state">
              Queue unavailable. <button className="chip" onClick={() => void refetch()}>Retry</button>
            </div>
          )}
          {posts && posts.length === 0 && (
            <div className="empty-state">Queue empty — all signals verified or none flagged.</div>
          )}
          {posts?.map((p) => (
            <QueueCard
              key={p.id}
              post={p}
              analyst={analyst}
              claims={verify.claims(p.id)}
              onClaim={() => verify.claim(p.id, analyst)}
              onComplete={() => verify.complete(p.id, analyst)}
              resolved={verify.resolved(p.id)}
            />
          ))}
        </div>

        <aside className="verify-rail">
          <div className="panel">
            <div className="panel__head">
              <span className="eyebrow">SCOREBOARD</span>
            </div>
            {leaderboard.length === 0 && <p className="dim">No verifications yet. Claim a slot — first one is free.</p>}
            {leaderboard.slice(0, 8).map(([name, pts], i) => (
              <div key={name} className="lb-line">
                <span className={`mono${i === 0 ? ' lb-line--top' : ''}`}>#{i + 1} {name}</span>
                <span className="mono dim">{pts} XP</span>
              </div>
            ))}
          </div>
          <div className="panel">
            <div className="panel__head">
              <span className="eyebrow">RULES OF THE HUNT</span>
            </div>
            <ul className="tips">
              <li><b>+10 XP</b> for claiming a slot, <b>+25 XP</b> for completing your verification.</li>
              <li>Two independent analysts must close a signal before it counts as <b>VERIFIED</b>.</li>
              <li>Flag wrong claims — the queue is only as good as its reviewers.</li>
              <li>Safety delay applies: never verify live troop positions or personnel locations.</li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
